import { Download, X, Share, Plus, Smartphone, Monitor } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { usePWAInstall } from '@/contexts/PWAInstallContext';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';

interface PWAInstallBannerProps {
  variant?: 'banner' | 'card' | 'button';
  className?: string;
}

export const PWAInstallBanner = ({ variant = 'banner', className }: PWAInstallBannerProps) => {
  const { t } = useTranslation();
  const {
    canInstall,
    isIOS,
    install,
    dismissBanner,
    shouldShowBanner,
    showIOSInstructions,
    setShowIOSInstructions,
  } = usePWAInstall();

  if (variant === 'button') {
    if (!canInstall) return null;

    return (
      <>
        <Button
          variant="outline"
          size="sm"
          onClick={install}
          className={cn("gap-2", className)}
        >
          <Download className="h-4 w-4" />
          {t('pwa.installButton')}
        </Button>
        <IOSInstructionsDialog
          open={showIOSInstructions}
          onOpenChange={setShowIOSInstructions}
        />
      </>
    );
  }

  if (variant === 'card') {
    if (!canInstall) return null;

    return (
      <>
        <Card className={cn("bg-primary/5 border-primary/20", className)}>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 rounded-full bg-primary/10 flex gap-1">
              <Smartphone className="h-5 w-5 text-primary" />
              <Monitor className="h-5 w-5 text-primary" />
            </div>
            <div className="flex-1">
              <h3 className="font-medium text-sm">{t('pwa.cardTitle')}</h3>
              <p className="text-xs text-muted-foreground mt-1">
                {t('pwa.cardDescription')}
              </p>
            </div>
            <Button size="sm" onClick={install}>
              <Download className="h-4 w-4 mr-2" />
              {t('pwa.install')}
            </Button>
          </CardContent>
        </Card>
        <IOSInstructionsDialog
          open={showIOSInstructions}
          onOpenChange={setShowIOSInstructions}
        />
      </>
    );
  }

  // Banner variant - only show if shouldShowBanner
  if (!shouldShowBanner) return null;

  return (
    <>
      <div
        className={cn(
          "fixed bottom-20 left-4 right-4 z-50 md:left-auto md:right-4 md:max-w-sm",
          "animate-in slide-in-from-bottom-4 duration-300",
          className
        )}
      >
        <Card className="shadow-lg border-primary/20 bg-background/95 backdrop-blur-sm">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <div className="p-2 rounded-full bg-primary/10 flex-shrink-0 flex gap-0.5">
                <Smartphone className="h-4 w-4 text-primary" />
                <Monitor className="h-4 w-4 text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-medium text-sm">{t('pwa.bannerTitle')}</h3>
                <p className="text-xs text-muted-foreground mt-1">
                  {isIOS ? t('pwa.bannerDescriptionIOS') : t('pwa.bannerDescription')}
                </p>
                <div className="flex gap-2 mt-3">
                  <Button size="sm" onClick={install} className="flex-1">
                    {isIOS ? t('pwa.showInstructions') : t('pwa.install')}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={dismissBanner}>
                    {t('pwa.later')}
                  </Button>
                </div>
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6 flex-shrink-0 -mt-1 -mr-1"
                onClick={dismissBanner}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
      <IOSInstructionsDialog
        open={showIOSInstructions}
        onOpenChange={setShowIOSInstructions}
      />
    </>
  );
};

// iOS-specific instructions dialog
export const IOSInstructionsDialog = ({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) => {
  const { t } = useTranslation();
  const onClose = () => onOpenChange(false);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Smartphone className="h-5 w-5" />
            {t('pwa.iosTitle')}
          </DialogTitle>
          <DialogDescription>
            {t('pwa.iosDescription')}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="flex items-start gap-4">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10 text-primary font-semibold text-sm">
              1
            </div>
            <div className="flex-1">
              <p className="font-medium text-sm">{t('pwa.iosStep1Title')}</p>
              <p className="text-sm text-muted-foreground flex items-center gap-2 mt-1">
                {t('pwa.iosStep1Desc')}
                <Share className="h-4 w-4" />
              </p>
            </div>
          </div>
          <div className="flex items-start gap-4">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10 text-primary font-semibold text-sm">
              2
            </div>
            <div className="flex-1">
              <p className="font-medium text-sm">{t('pwa.iosStep2Title')}</p>
              <p className="text-sm text-muted-foreground flex items-center gap-2 mt-1">
                {t('pwa.iosStep2Desc')}
                <Plus className="h-4 w-4" />
              </p>
            </div>
          </div>
          <div className="flex items-start gap-4">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10 text-primary font-semibold text-sm">
              3
            </div>
            <div className="flex-1">
              <p className="font-medium text-sm">{t('pwa.iosStep3Title')}</p>
              <p className="text-sm text-muted-foreground">
                {t('pwa.iosStep3Desc')}
              </p>
            </div>
          </div>
        </div>
        <Button onClick={onClose} className="w-full">
          {t('pwa.gotIt')}
        </Button>
      </DialogContent>
    </Dialog>
  );
};

// Navbar install button (compact)
export const NavbarInstallButton = () => {
  const { canInstall, install, isIOS, showIOSInstructions, setShowIOSInstructions } = usePWAInstall();
  const { t } = useTranslation();

  if (!canInstall) return null;

  return (
    <>
      <Button
        variant="ghost"
        size="sm"
        onClick={install}
        className="gap-2 text-muted-foreground hover:text-foreground"
      >
        <Download className="h-4 w-4" />
        <span className="hidden sm:inline">{t('pwa.installButton')}</span>
      </Button>
      <IOSInstructionsDialog
        open={showIOSInstructions}
        onOpenChange={setShowIOSInstructions}
      />
    </>
  );
};